import { useCallback, useState } from "react";
import { CurrencyCode, PaymentMethod } from "#graphql/supergraphEnums";
import { useGetPayeeForBankAccountDetailsQuery } from "#graphql/supergraphHooks";
import { clientContext } from "#utils/graphql_utils";
import { stringToPaymentMethod } from "../../DistributionWorkflow/utils";
import { usePayableDataContext } from "../Context/PayableContext";
import { useCreatePayableHandler } from "./useCreatePayableHandler";
import { useCreatePayablePaymentBatchHandler } from "./useCreatePayablePaymentBatchHandler";

export interface UseSubmitPayableAndPaymentOptions {
  onSuccess?: (paymentBatchId: string) => void;
}

export interface SubmitPaymentDetails {
  paymentAmount: number;
  paymentDate: Date | null;
  paymentDescription: string | null;
}

export interface SubmitPayableAndPaymentResult {
  paymentBatchId?: string;
  error?: string;
}

export interface UseSubmitPayableAndPaymentReturn {
  submit: (paymentDetails: SubmitPaymentDetails) => Promise<SubmitPayableAndPaymentResult>;
  loading: boolean;
  error: string | null;
}

/**
 * Hook that orchestrates sequential API calls for payable and payment creation.
 * Creates payable first, then creates payment batch.
 * Handles partial failure (payable succeeds but payment fails) with retry capability.
 */
export const useSubmitPayableAndPayment = (
  options: UseSubmitPayableAndPaymentOptions = {},
): UseSubmitPayableAndPaymentReturn => {
  const { onSuccess } = options;
  const payableData = usePayableDataContext();

  const [error, setError] = useState<string | null>(null);

  const { createPayable, loading: payableLoading } = useCreatePayableHandler();
  const { createBatch, loading: paymentLoading } = useCreatePayablePaymentBatchHandler();

  // Fetch payee details to get payment method
  const { data: payeeDetails } = useGetPayeeForBankAccountDetailsQuery({
    variables: { payeeId: payableData.payeeId ?? "" },
    skip: !payableData.payeeId,
    context: clientContext(),
    fetchPolicy: "cache-first",
  });

  const getPaymentMethod = useCallback((): PaymentMethod => {
    const paymentMethodStr = payeeDetails?.payee?.paymentPref?.paymentMethod;
    return stringToPaymentMethod(paymentMethodStr) ?? PaymentMethod.Wire;
  }, [payeeDetails]);

  type CreatePaymentResult =
    | { paymentBatchId: string; error?: undefined }
    | { paymentBatchId?: undefined; error: string };

  const createPaymentForPayable = useCallback(
    async (
      payableId: string,
      paymentDetails: SubmitPaymentDetails,
    ): Promise<CreatePaymentResult> => {
      const paymentMethod = getPaymentMethod();

      // ACH validation: check entity bank account capabilities
      if (paymentMethod === PaymentMethod.Ach) {
        const paymentCapabilities = payableData.entityPaymentCapabilities;

        if (!paymentCapabilities?.achPayments) {
          const errorMsg = "ACH payments are not enabled for this bank account.";
          setError(errorMsg);
          return { error: errorMsg };
        }

        if (payableData.entityAchCompanyIdPresent !== true) {
          const errorMsg = "ACH payments require a company ID for this bank account.";
          setError(errorMsg);
          return { error: errorMsg };
        }
      }

      // Use payment details passed directly from form (not from context which may be stale)
      const paymentAmount = paymentDetails.paymentAmount;
      if (!paymentAmount || paymentAmount <= 0) {
        const errorMsg = "Valid payment amount is required";
        setError(errorMsg);
        return { error: errorMsg };
      }

      const result = await createBatch({
        payableId,
        payeeId: payableData.payeeId!,
        amount: {
          value: paymentAmount,
          precision: 2,
          currency: CurrencyCode.Usd,
        },
        paymentMethod,
        paymentDescription: paymentDetails.paymentDescription ?? null,
        paymentDate: paymentDetails.paymentDate ?? null,
      });

      if (result.error !== undefined) {
        setError(result.error);
        return { error: result.error };
      }

      if (!result.paymentBatchId) {
        const errorMsg = "Failed to create payment batch";
        setError(errorMsg);
        return { error: errorMsg };
      }

      return { paymentBatchId: result.paymentBatchId };
    },
    [
      createBatch,
      getPaymentMethod,
      payableData.entityAchCompanyIdPresent,
      payableData.entityPaymentCapabilities,
      payableData.payeeId,
    ],
  );

  const submit = useCallback(
    async (paymentDetails: SubmitPaymentDetails): Promise<SubmitPayableAndPaymentResult> => {
      setError(null);

      // Step 1: Create payable - merge payableData with fresh payment details from form
      // Convert null to undefined for paymentDescription to match PayableData type
      const payableDataWithPaymentDetails = {
        ...payableData,
        paymentAmount: paymentDetails.paymentAmount,
        paymentDate: paymentDetails.paymentDate,
        paymentDescription: paymentDetails.paymentDescription ?? undefined,
      };
      const createPayableResult = await createPayable(payableDataWithPaymentDetails);

      if (createPayableResult.error) {
        setError(createPayableResult.error);
        return { error: createPayableResult.error };
      }

      if (!createPayableResult.payableId) {
        const errorMsg = "Failed to create payable";
        setError(errorMsg);
        return { error: errorMsg };
      }

      // Step 2: Create payment batch with fresh payment details
      const paymentResult = await createPaymentForPayable(
        createPayableResult.payableId,
        paymentDetails,
      );

      if (paymentResult.error !== undefined) {
        setError(paymentResult.error);
        return { error: paymentResult.error };
      }

      // Success! At this point paymentBatchId is guaranteed to be defined
      const paymentBatchId = paymentResult.paymentBatchId;
      onSuccess?.(paymentBatchId);
      return { paymentBatchId };
    },
    [createPayable, createPaymentForPayable, onSuccess, payableData],
  );

  return {
    submit,
    loading: payableLoading || paymentLoading,
    error,
  };
};
